import {
  getConfidenceColor,
  formatConfidencePercentage,
} from "../utils/confidenceUtils";
import { sortWordsByPosition } from "../utils/gridCalculationUtils";
import { FilterAltOff } from "@mui/icons-material";
import { Popover, Box, Typography, Tooltip, useTheme } from "@mui/material";
import React, { memo } from "react";

/**
 * CellInfoPopover Component - Shows detailed information about a selected cell
 * Displays cell content, page info, and individual words
 * Extracted from main PdfTableViewer to improve maintainability
 */
const CellInfoPopover = ({
  // Popover state
  open,
  anchorEl,
  onClose,

  // Cell data
  selectedCell,
  selectedWordIndex,

  // Event handlers
  onWordClick,

  // Confidence filtering
  confidenceFilterEnabled,
  confidenceThreshold,
}) => {
  const theme = useTheme();
  const defaultBorderColor = theme.brand.gray.gainsboro;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "right",
      }}
    >
      <Box sx={{ p: 2, maxWidth: 400 }}>
        {selectedCell && (
          <>
            <Typography variant={"h6"} gutterBottom>
              {`${selectedCell.row === 0 ? "Header" : ""}${
                selectedCell.row === 0 ? " " : ""
              }Row ${selectedCell.row}, Col ${selectedCell.col}`}
            </Typography>

            <Typography variant={"body2"} color={"text.secondary"}>
              {"Text:"}
            </Typography>
            <Typography variant={"body1"} sx={{ fontWeight: "medium", mb: 1 }}>
              {selectedCell.text && selectedCell.text.trim()
                ? selectedCell.text
                : "Empty"}
            </Typography>

            <Typography variant={"body2"} color={"text.secondary"}>
              {`Page: ${selectedCell.page} | Words: ${selectedCell.words.length}`}
            </Typography>

            {selectedCell.words.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant={"body2"}
                  color={"text.secondary"}
                  sx={{ mb: 1 }}
                >
                  {"Individual Words:"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {sortWordsByPosition([...selectedCell.words]).map(
                    (word, index) => {
                      const confidenceColors = getConfidenceColor(
                        word.confidence
                      );
                      const isSelected = selectedWordIndex === index;

                      // Check if word is filtered out
                      const isFilteredOut =
                        confidenceFilterEnabled &&
                        word.confidence !== undefined &&
                        word.confidence !== null &&
                        word.confidence < confidenceThreshold;

                      // Default styles for high confidence (when confidenceColors is null)
                      const defaultBackgroundColor = "#f9f9f9";
                      const defaultColor = "inherit";

                      // Override styles for filtered-out words
                      const filteredBorderColor = "#ccc";
                      const filteredBackgroundColor = "#f5f5f5";
                      const filteredColor = "#999";

                      const wordBox = (
                        <Box
                          key={index}
                          onClick={() => onWordClick(index)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: isSelected ? "2px solid" : "1px solid",
                            borderColor: isSelected
                              ? "#2196f3"
                              : isFilteredOut
                              ? filteredBorderColor
                              : confidenceColors?.borderColor ||
                                defaultBorderColor,
                            backgroundColor: isSelected
                              ? "rgba(33, 150, 243, 0.1)"
                              : isFilteredOut
                              ? filteredBackgroundColor
                              : confidenceColors?.backgroundColor ||
                                defaultBackgroundColor,
                            color: isSelected
                              ? "#1976d2"
                              : isFilteredOut
                              ? filteredColor
                              : confidenceColors?.color || defaultColor,
                            opacity: isFilteredOut ? 0.6 : 1,
                            "&:hover": {
                              borderColor: isSelected ? "#1976d2" : "#90a4ae",
                              backgroundColor: isSelected
                                ? "rgba(33, 150, 243, 0.15)"
                                : isFilteredOut
                                ? `${filteredBackgroundColor}dd`
                                : `${
                                    confidenceColors?.backgroundColor ||
                                    defaultBackgroundColor
                                  }dd`,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                              opacity: isFilteredOut ? 0.8 : 1,
                            },
                            transition: "all 0.15s ease-out",
                            minWidth: "50px",
                            position: "relative",
                          }}
                        >
                          <Typography
                            variant={"body2"}
                            sx={{
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: "0.875rem",
                              textAlign: "center",
                              letterSpacing: "0.025em",
                              lineHeight: 1.2,
                              textDecoration: isFilteredOut
                                ? "line-through"
                                : "none",
                              textDecorationColor: isFilteredOut
                                ? "#666"
                                : "inherit",
                              textDecorationThickness: isFilteredOut
                                ? "2px"
                                : "auto",
                            }}
                          >
                            {word.text}
                          </Typography>
                          {isFilteredOut && (
                            <FilterAltOff
                              sx={{
                                position: "absolute",
                                top: "-4px",
                                right: "-4px",
                                fontSize: "14px",
                                color: "#999",
                              }}
                            />
                          )}
                        </Box>
                      );

                      // Wrap in tooltip if filtered out
                      if (isFilteredOut) {
                        return (
                          <Tooltip
                            key={index}
                            title={`Filtered out (${formatConfidencePercentage(
                              word.confidence
                            )} < ${(confidenceThreshold * 100).toFixed(0)}%)`}
                            arrow
                          >
                            {wordBox}
                          </Tooltip>
                        );
                      }

                      // Regular tooltip for non-filtered words
                      return (
                        <Tooltip
                          key={index}
                          title={`Confidence: ${formatConfidencePercentage(
                            word.confidence
                          )}`}
                        >
                          {wordBox}
                        </Tooltip>
                      );
                    }
                  )}
                </Box>

                {/* Confidence Legend */}
                <Box
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography
                    variant={"caption"}
                    color={"text.primary"}
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      display: "block",
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {"Confidence Score"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "2px",
                          backgroundColor: "rgba(211, 47, 47, 0.25)",
                          border: "1px solid rgba(183, 28, 28, 0.7)",
                        }}
                      />
                      <Typography
                        variant={"caption"}
                        color={"text.secondary"}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {"Low (<50%)"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "2px",
                          backgroundColor: "rgba(255, 193, 7, 0.25)",
                          border: "1px solid rgba(255, 152, 0, 0.7)",
                        }}
                      />
                      <Typography
                        variant={"caption"}
                        color={"text.secondary"}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {"Medium (50-89%)"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "2px",
                          backgroundColor: "transparent",
                          border: `1px solid ${defaultBorderColor}`,
                        }}
                      />
                      <Typography
                        variant={"caption"}
                        color={"text.secondary"}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {"High (≥90%)"}
                      </Typography>
                    </Box>
                    {confidenceFilterEnabled && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "2px",
                            backgroundColor: "#f5f5f5",
                            border: "1px solid #ccc",
                            position: "relative",
                          }}
                        >
                          <FilterAltOff
                            sx={{
                              position: "absolute",
                              top: "-6px",
                              right: "-6px",
                              fontSize: "12px",
                              color: "#999",
                            }}
                          />
                        </Box>
                        <Typography
                          variant={"caption"}
                          color={"text.secondary"}
                          sx={{
                            fontSize: "0.75rem",
                            textDecoration: "line-through",
                          }}
                        >
                          {`Filtered (<${(confidenceThreshold * 100).toFixed(
                            0
                          )}%)`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    sx={{
                      fontSize: "0.7rem",
                      fontStyle: "italic",
                    }}
                  >
                    {"Hover words for exact values"}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Popover>
  );
};

export default memo(CellInfoPopover);
